import React from 'react'
import 'views/views.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import {
    BarChart as RechartBarChart,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    ResponsiveContainer,
    Legend,
    LabelList,
    // CartesianGrid,
} from 'recharts'
import { LoadingIndicator } from 'components'
import { clientTheme } from 'theme-exports'

// Format percentage values based on their magnitude
const formatPercentTick = (val: number): string => {
    if (val === 0) {
        return '0%'
    }
    if (val < 1) {
        return `${val.toFixed(2)}%`
    }
    return `${val.toFixed(1)}%`
}

const CustomTooltipPercent = ({
    active,
    payload,
    label,
}: {
    [key: string]: any
}) => {
    if (active && payload?.length) {
        return (
            <div
                className={'emp-barChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ backgroundColor: '#b3b3b3' }}>
                            <td className={'emp-barChart-tooltipTableHeader'}>
                                {label}
                            </td>
                        </tr>

                        <tr>
                            <td
                                className={'emp-barChart-tooltipTableRow'}
                                style={{
                                    textAlign: 'center',
                                }}
                            >
                                {formatPercentTick(payload[0].value)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    }

    return null
}

const CustomTooltipValue = ({
    active,
    payload,
    label,
    colorPalette,
    barLineColor,
}: {
    colorPalette: string[]
    barLineColor: string
    [key: string]: any
}) => {
    if (active && payload?.length) {
        return (
            <div
                className={'emp-barChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ backgroundColor: '#b3b3b3' }}>
                            <td
                                colSpan={2}
                                className={'emp-barChart-tooltipTableHeader'}
                            >
                                {label}
                            </td>
                        </tr>
                        {payload.map((item: any, idx: number) => {
                            return (
                                <tr key={`key-tooltip-${item.name}-${idx}`}>
                                    <td
                                        className={
                                            'emp-barChart-tooltipTableRow'
                                        }
                                        style={{
                                            borderRight: '1px dotted #b3b3b3',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            size="xs"
                                            icon={faSquare}
                                            style={{
                                                marginRight: '5px',
                                                color: colorPalette.length
                                                    ? item.fill
                                                    : barLineColor,
                                            }}
                                        />
                                        {item.name}
                                    </td>
                                    <td
                                        className={
                                            'emp-barChart-tooltipTableRow'
                                        }
                                        style={{
                                            textAlign: 'right',
                                        }}
                                    >
                                        {item.value}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }
    return null
}

/**
 * @name BarChart
 */
const BarChart = ({
    data = [],
    loading = false,
    valueDataKey = undefined,
    barLineColor = '#b71c1c',
    showLegend = false,
    stackedBarValueKeys = [],
    colorPalette = [],
    dataValuePercent = false,
    height = 225,
    xAxisAngle = false,
    disableYAxisAutoDomain = false,
    customYAxisDomain = undefined,
    customYAxisTickFormatter = undefined,
    enableVerticalChart = true,
}: {
    data: any[]
    loading?: boolean
    valueDataKey?: undefined | string
    barLineColor?: string
    showLegend?: boolean
    stackedBarValueKeys?: [string, number][]
    colorPalette?: string[]
    dataValuePercent?: boolean
    height?: number
    xAxisAngle?: boolean
    disableYAxisAutoDomain?: boolean
    customYAxisDomain?: [number, number] // Custom domain for Y-axis [min, max]
    customYAxisTickFormatter?: (value: number) => string
    enableVerticalChart?: boolean
}): React.ReactElement => {
    const renderHorizontalLabel = ({ x, y, value }: { [key: string]: any }) => {
        return (
            <g>
                <text
                    x={x + 5}
                    y={y - 3}
                    textAnchor="left"
                    fill={
                        clientTheme.typography.fontColor.primaryText ||
                        '#263238'
                    }
                    fontSize={clientTheme.typography.body1.fontSize}
                >
                    {value}
                </text>
            </g>
        )
    }

    return (
        <div className={'emp-barChart-root'}>
            {!loading ? (
                <>
                    {data.length ? (
                        <ResponsiveContainer
                            height={xAxisAngle ? 265 : height}
                            width="100%"
                        >
                            {enableVerticalChart ? (
                                <RechartBarChart
                                    data={data}
                                    stackOffset={
                                        dataValuePercent ? 'none' : 'expand'
                                    }
                                    margin={{
                                        top: 20,
                                        left: -5,
                                        bottom: 5,
                                        right: xAxisAngle ? 25 : 0,
                                    }}
                                >
                                    {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                    {xAxisAngle ? (
                                        <XAxis
                                            dataKey="name"
                                            angle={45}
                                            textAnchor="start"
                                            height={70}
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                            interval={0}
                                        />
                                    ) : (
                                        <XAxis
                                            dataKey="name"
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    )}
                                    {dataValuePercent ? (
                                        <YAxis
                                            tickFormatter={formatPercentTick}
                                            domain={
                                                customYAxisDomain ||
                                                (disableYAxisAutoDomain
                                                    ? undefined
                                                    : dataValuePercent
                                                    ? [
                                                          0,
                                                          (dataMax) =>
                                                              Math.min(
                                                                  Math.round(
                                                                      dataMax *
                                                                          1.1
                                                                  ),
                                                                  100
                                                              ),
                                                      ]
                                                    : undefined)
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    ) : (
                                        <YAxis
                                            tickFormatter={
                                                customYAxisTickFormatter
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    )}
                                    {dataValuePercent ? (
                                        <Tooltip
                                            content={<CustomTooltipPercent />}
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    ) : (
                                        <Tooltip
                                            content={
                                                <CustomTooltipValue
                                                    colorPalette={colorPalette}
                                                    barLineColor={barLineColor}
                                                />
                                            }
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    )}
                                    {dataValuePercent ||
                                    !stackedBarValueKeys.length ? (
                                        <Bar
                                            dataKey={
                                                valueDataKey
                                                    ? valueDataKey
                                                    : 'val'
                                            }
                                            fill={barLineColor}
                                            maxBarSize={100}
                                        />
                                    ) : (
                                        stackedBarValueKeys.map(
                                            (bar: any, index: number) => {
                                                return (
                                                    <Bar
                                                        dataKey={bar[0]}
                                                        key={`${index}_bar-line`}
                                                        fill={
                                                            colorPalette[
                                                                index %
                                                                    colorPalette.length
                                                            ] || '#b71c1c'
                                                        }
                                                        stackId="a"
                                                        maxBarSize={100}
                                                    />
                                                )
                                            }
                                        )
                                    )}

                                    {showLegend ? (
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            height={55}
                                            wrapperStyle={{
                                                left: 27,
                                                fontSize:
                                                    clientTheme.typography.body1
                                                        .fontSize,
                                            }}
                                        />
                                    ) : null}
                                </RechartBarChart>
                            ) : (
                                <RechartBarChart
                                    data={data}
                                    stackOffset={
                                        dataValuePercent ? 'none' : 'expand'
                                    }
                                    margin={{
                                        top: 20,
                                        left: -50,
                                        bottom: 5,
                                        right: 0,
                                    }}
                                    layout="vertical"
                                    barCategoryGap="27%"
                                >
                                    {/* <CartesianGrid strokeDasharray="3 3" /> */}

                                    <YAxis
                                        dataKey="name"
                                        tick={false}
                                        type="category"
                                        tickLine={false}
                                    />

                                    {dataValuePercent ? (
                                        <XAxis
                                            tickFormatter={formatPercentTick}
                                            domain={
                                                customYAxisDomain ||
                                                (disableYAxisAutoDomain
                                                    ? undefined
                                                    : dataValuePercent
                                                    ? [
                                                          0,
                                                          (dataMax) =>
                                                              Math.min(
                                                                  Math.round(
                                                                      dataMax *
                                                                          1.1
                                                                  ),
                                                                  100
                                                              ),
                                                      ]
                                                    : undefined)
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                            type="number"
                                        />
                                    ) : (
                                        <XAxis
                                            tickFormatter={
                                                customYAxisTickFormatter
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                            type="number"
                                        />
                                    )}
                                    {dataValuePercent ? (
                                        <Tooltip
                                            content={<CustomTooltipPercent />}
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    ) : (
                                        <Tooltip
                                            content={
                                                <CustomTooltipValue
                                                    colorPalette={colorPalette}
                                                    barLineColor={barLineColor}
                                                />
                                            }
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    )}
                                    {dataValuePercent ||
                                    !stackedBarValueKeys.length ? (
                                        <Bar
                                            dataKey={
                                                valueDataKey
                                                    ? valueDataKey
                                                    : 'val'
                                            }
                                            fill={barLineColor}
                                            maxBarSize={100}
                                        >
                                            <LabelList
                                                dataKey="name"
                                                content={renderHorizontalLabel}
                                            />
                                        </Bar>
                                    ) : (
                                        stackedBarValueKeys.map(
                                            (bar: any, index: number) => {
                                                return (
                                                    <Bar
                                                        dataKey={bar[0]}
                                                        key={`${index}_bar-line`}
                                                        fill={
                                                            colorPalette[
                                                                index %
                                                                    colorPalette.length
                                                            ] || '#b71c1c'
                                                        }
                                                        stackId="a"
                                                        maxBarSize={100}
                                                    >
                                                        <LabelList
                                                            dataKey="name"
                                                            content={
                                                                renderHorizontalLabel
                                                            }
                                                        />
                                                    </Bar>
                                                )
                                            }
                                        )
                                    )}

                                    {showLegend ? (
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            height={55}
                                            wrapperStyle={{
                                                left: 27,
                                                fontSize:
                                                    clientTheme.typography.body1
                                                        .fontSize,
                                            }}
                                        />
                                    ) : null}
                                </RechartBarChart>
                            )}
                        </ResponsiveContainer>
                    ) : (
                        <div className={'emp-barChart-noDataMessaging'}>
                            No Data
                        </div>
                    )}
                </>
            ) : (
                <div className={'emp-barChart-loadingContainer'}>
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default BarChart
